import {
  Button,
  ButtonGroup,
  IconButton,
  IconLink,
  IconLinkOutline,
  IconMail,
  IconMore,
  IconShare,
  Menu,
  MenuItem,
  MenuPopper,
  Toast,
  withToast,
} from '@zillow/constellation';
import { getGenericApplicationUrl } from '../../constants/urls';
import type React from 'react';
import { inviteRenterToApplyClick } from '../../actions/invitationActionCreators';
import { useDispatch, useSelector } from 'react-redux';
import { isMobileSelector, isZtssSelector, listingAliasSelector } from '../../selectors';
import useCopyApplicationLink from '../../utils/apis/useCopyApplicationLink';
import UtmGuids from '../../constants/utmGuids';
import { emitShareLinkMobileWebEvent } from '../../../client/analytics/clickstreamEvents';
import { useListingUser } from '../../hooks/useListingUser';

interface InviteToApplyLinksProps {
  enqueueToast?: (toast: React.ReactElement) => void;
  onInviteClickAnalyticsCallback?: () => void;
}

function InviteToApplyLinks({ enqueueToast, onInviteClickAnalyticsCallback }: InviteToApplyLinksProps) {
  const dispatch = useDispatch();
  const listingAlias = useSelector(listingAliasSelector);
  const isMobile = useSelector(isMobileSelector);
  const { listing, user } = useListingUser();
  const isZtss = useSelector(isZtssSelector);
  let applicationLink =
    typeof window !== 'undefined' ? getGenericApplicationUrl(window.location.host, listingAlias) : '';
  if (isZtss) {
    const utm_source = localStorage?.getItem('sia_utm_source') ?? '';
    const utm_content = localStorage?.getItem('sia_utm_content') ?? '';
    applicationLink = applicationLink.concat(`&utm_guid=${UtmGuids.siaExperianReferral}${utm_source}${utm_content}`);
  }
  const { data } = useCopyApplicationLink(listingAlias, applicationLink, {
    initialData: {
      response: applicationLink,
    },
  });

  function onInviteButtonClick() {
    onInviteClickAnalyticsCallback?.();
    return dispatch(inviteRenterToApplyClick());
  }

  async function copyLinkToClipboard() {
    await navigator.clipboard
      .writeText((data as any)?.data.response)
      .then(() => {
        enqueueToast?.(<Toast appearance="success" body="Link copied" />);
      })
      .catch(() => {
        enqueueToast?.(<Toast appearance="error" body="Unable to copy link. Please try again." />);
      });
  }

  async function shareLinkOnMobile() {
    emitShareLinkMobileWebEvent(listing, user);

    try {
      navigator.share({
        text: 'Click here to apply to my Zillow Rentals listing!',
        url: getGenericApplicationUrl(window.location.host, listingAlias),
      });
    } catch {
      enqueueToast?.(<Toast appearance="error" body="Unable to share link." />);
    }
  }

  const mobileMenu = (
    <Menu aria-label="Sharing links">
      <MenuItem icon={<IconLinkOutline />} onClick={copyLinkToClipboard}>
        Copy link
      </MenuItem>
      <MenuItem icon={<IconShare />} onClick={shareLinkOnMobile}>
        Share with other apps
      </MenuItem>
    </Menu>
  );

  return (
    <ButtonGroup aria-label="invite to apply buttons" marginTop="sm">
      <Button buttonType="primary" icon={<IconMail />} onClick={onInviteButtonClick}>
        Send application link
      </Button>
      {isMobile ? (
        <MenuPopper aria-label="Sharing options button" triggered={mobileMenu}>
          <IconButton title="Sharing options" buttonType="secondary" icon={<IconMore />} />
        </MenuPopper>
      ) : (
        <IconButton
          buttonType="secondary"
          icon={<IconLink />}
          aria-label="Copy link"
          title="Copy link"
          onClick={copyLinkToClipboard}
        />
      )}
    </ButtonGroup>
  );
}

export default withToast(InviteToApplyLinks);
